import React, { useEffect, useState, useRef, useCallback } from 'react';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Reply from '@mui/icons-material/Reply';
import axios from '../axiosConfig';
import './ProductsList.css';
import { completeImageUrl } from '../utils/imageUrl';
import VariationPopup from './VariationPopup';
import ConfirmPopup from './ConfirmPopup';
import FilterMenu from './FilterMenu';
import HeartIcon from './HeartIcon';
import ProductInfoContent from './ProductInfoContent';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProductImageUrl } from '../utils/productImage';
import { formatSizeSpaced } from '../utils/formatSize';
import FeedCaptionMeta from './FeedCaptionMeta';


export default function ProductsList({ endpoint, cartMode = false, onOpenAuth }) {
  const [products, setProducts] = useState([]);
  const [likedIds, setLikedIds] = useState([]);
  const [cartIds, setCartIds] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentUserAccountId, setCurrentUserAccountId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [toasts, setToasts] = useState([]);
  // Which feed slide is currently in view - used to run the caption's
  // backdrop-blur ONLY on the active card (off-screen cards all running
  // backdrop-filter was the scroll-jank cause).
  const [activeProductId, setActiveProductId] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    priceRange: { min: '', max: '' },
    sortBy: 'newest'
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 12; // Multiple of 3 for 3-column grid
  
  
  // Mobile interaction state
  const [clickTimeouts, setClickTimeouts] = useState({});

  // Observe which feed slide is in view so the caption's backdrop-blur runs
  // only on the active card (not every mounted one). Re-observes when the
  // product list grows (pagination). Active state changes at most once per
  // slide transition - not per frame - so it's cheap.
  useEffect(() => {
    if (!products.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const id = entry.target.getAttribute('data-product-id');
            if (id) setActiveProductId(Number(id));
          }
        });
      },
      { threshold: [0.6] }
    );
    document
      .querySelectorAll('.products__link[data-product-id]')
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [products]);

  // React Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state || {};

  // Intersection observer ref for infinite scroll
  const observerRef = useRef();
  // Ref to track last handled scrollTo to prevent double handling
  const lastHandledScrollToRef = useRef(null);
  // Ref to store the last search params (excluding scrollTo) for comparison
  const lastSearchParamsRef = useRef('');
  const loadMoreProducts = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const params = new URLSearchParams();
      
      // Add pagination parameters
      params.set('page', nextPage.toString());
      params.set('per_page', productsPerPage.toString());
      
      // Add filter parameters
      if (filters.category) {
        params.set('category', filters.category);
      }
      if (filters.priceRange.min) {
        params.set('min_price', filters.priceRange.min);
      }
      if (filters.priceRange.max) {
        params.set('max_price', filters.priceRange.max);
      }
      if (filters.sortBy) {
        params.set('sort', filters.sortBy);
      }
      
      // Merge with existing URL parameters
      const currentParams = new URLSearchParams(location.search);
      for (const [key, value] of currentParams.entries()) {
        if (!params.has(key)) {
          params.set(key, value);
        }
      }

      const response = await axios.get(`${endpoint}?${params.toString()}`);
      const newProducts = response.data;
      
      if (newProducts.length > 0) {
        setProducts(prev => [...prev, ...newProducts]);
        setPage(nextPage);
        
        // Update liked and cart states for new products
        if (localStorage.getItem('authToken')) {
          const newLikedIds = newProducts
            .filter((p) => p.liked_by_current_user)
            .map((p) => p.id);
          const newCartIds = newProducts
            .filter((p) => p.in_cart)
            .map((p) => p.id);
          
          setLikedIds(prev => [...prev, ...newLikedIds]);
          setCartIds(prev => [...prev, ...newCartIds]);
          
          const newQuantities = {};
          newProducts.forEach((p) => {
            if (newCartIds.includes(p.id)) {
              newQuantities[p.id] = p.chosen_variation?.quantity || 1;
            }
          });
          setQuantities(prev => ({ ...prev, ...newQuantities }));
        }
        
        setHasMore(newProducts.length >= productsPerPage);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, productsPerPage, filters, endpoint, location.search, setLikedIds, setCartIds, setQuantities]);

  const lastProductRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreProducts();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasMore, loadMoreProducts]);

  // Для показу вікна вибору варіації при додаванні товару з декількома варіаціями.
  const [showVariationPopup, setShowVariationPopup] = useState(false);
  const [popupVariations] = useState([]);
  const [popupProductId, setPopupProductId] = useState(null);
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [buyDialogProduct, setBuyDialogProduct] = useState(null);
  const [buyDialogQuantity, setBuyDialogQuantity] = useState(1);
  
  // Selected variations for each product (for inline variation selector)
  const [selectedVariations, setSelectedVariations] = useState({});

  // Для підтвердження видалення (видалення з кошику)
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [deleteAction, setDeleteAction] = useState(''); // 'decrease' або 'toggle'

  // Info state - track which product is showing info (flipped)
  const [flippedProductId, setFlippedProductId] = useState(null);

  // Prevent body scrolling when info is open
  useEffect(() => {
    if (flippedProductId !== null) {
      // Store the current scroll position
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Lock scrolling on all scrollable elements
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollTop}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Find and lock the page container
      const pageElements = document.querySelectorAll('.products__page, .products__grid');
      pageElements.forEach(el => {
        el.style.overflow = 'hidden';
        el.style.touchAction = 'none';
      });
    } else {
      // Restore the scroll position
      const scrollTop = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      
      if (scrollTop) {
        window.scrollTo(0, parseInt(scrollTop || '0') * -1);
      }
      
      // Unlock page elements
      const pageElements = document.querySelectorAll('.products__page, .products__grid');
      pageElements.forEach(el => {
        el.style.overflow = '';
        el.style.touchAction = '';
      });
    }
  }, [flippedProductId]);

  // Перевірка, чи перебуваємо в режимі "обране" (припускається, що endpoint містить "liked")
  const isLikedMode = endpoint.toLowerCase().includes('favorites');


  // Reset scroll position to top when component mounts or endpoint changes
  // When navigating from profile, we reorder products so clicked product is first,
  // then we want to ensure we're at the top (so clicked product is visible)
  useEffect(() => {
    const resetScroll = () => {
      // Reset window scroll
      window.scrollTo(0, 0);
      
      // Reset document element scroll (for some browsers)
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
      
      // Reset body scroll (for some browsers)
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
      
      // Reset any scrollable containers
      const scrollableElements = document.querySelectorAll('.products__page, main, [style*="overflow"]');
      scrollableElements.forEach(element => {
        if (element.scrollTop !== undefined) {
          element.scrollTop = 0;
        }
        if (element.scrollLeft !== undefined) {
          element.scrollLeft = 0;
        }
      });
    };
    
    resetScroll();
    
    // Reset after content loads
    if (!loading) {
      setTimeout(resetScroll, 50);
    }
    
    // Reset after a longer delay to ensure everything is rendered
    const timer = setTimeout(resetScroll, 200);
    
    return () => clearTimeout(timer);
  }, [endpoint, loading, locationState.scrollToProductId, location.search]);

  // Additional scroll reset on mount
  useEffect(() => {
    
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    resetScroll();
    
    // Also reset after a short delay to ensure content is rendered
    const timer = setTimeout(resetScroll, 100);
    
    return () => clearTimeout(timer);
  }, [locationState.scrollToProductId, location.search]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/api/v1/categories');
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);



  useEffect(() => {
    // Build search params without scrollTo for comparison
    const currentParams = new URLSearchParams(location.search);
    const paramsWithoutScrollTo = new URLSearchParams();
    for (const [key, value] of currentParams.entries()) {
      if (key !== 'scrollTo') {
        paramsWithoutScrollTo.set(key, value);
      }
    }
    const searchKey = paramsWithoutScrollTo.toString();
    
    // Skip fetch if only scrollTo parameter changed (no actual filter/query change)
    // Allow first fetch (when lastSearchParamsRef is empty)
    if (lastSearchParamsRef.current !== '' && lastSearchParamsRef.current === searchKey) {
      return;
    }
    lastSearchParamsRef.current = searchKey;
    
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setPage(1);
        setHasMore(true);
        
        // Build query parameters (exclude scrollTo from API params)
        const params = new URLSearchParams();
        
        // Add pagination parameters
        params.set('page', '1');
        params.set('per_page', productsPerPage.toString());
        
        // Add filter parameters
        if (filters.category) {
          params.set('category', filters.category);
        }
        if (filters.priceRange.min) {
          params.set('min_price', filters.priceRange.min);
        }
        if (filters.priceRange.max) {
          params.set('max_price', filters.priceRange.max);
        }
        if (filters.sortBy) {
          params.set('sort', filters.sortBy);
        }
        
        // Merge with existing URL parameters (excluding scrollTo)
        for (const [key, value] of paramsWithoutScrollTo.entries()) {
          if (!params.has(key)) {
            params.set(key, value);
          }
        }
        
        // Use axios with the endpoint and query parameters
        const response = await axios.get(`${endpoint}?${params.toString()}`);
        const fetchedProducts = response.data;
        
        setProducts(fetchedProducts);
        
        // Check if we have more products to load
        if (fetchedProducts.length < productsPerPage) {
          setHasMore(false);
        }
        
        if (localStorage.getItem('authToken')) {
          // Fetch current user account_id
          try {
            const userResponse = await axios.get('/api/v1/users/current');
            if (userResponse.data.account_id) {
              setCurrentUserAccountId(userResponse.data.account_id);
            }
          } catch (error) {
            console.error('Error fetching user account_id:', error);
          }
          
          const initialLiked = fetchedProducts
            .filter((p) => p.liked_by_current_user)
            .map((p) => p.id);
          const initialCart = fetchedProducts
            .filter((p) => p.in_cart)
            .map((p) => p.id);
          setLikedIds(initialLiked);
          setCartIds(initialCart);
          const initialQuantities = {};
          fetchedProducts.forEach((p) => {
            if (initialCart.includes(p.id)) {
              initialQuantities[p.id] = p.chosen_variation?.quantity || 1;
            }
          });
          setQuantities(initialQuantities);
          
          // Initialize selected variations for each product
          const initialVariations = {};
          fetchedProducts.forEach((product) => {
            if (product.product_variations && product.product_variations.length > 0) {
              // Use chosen variation if available, or main variation, or first variation
              const chosenVariation = product.chosen_variation;
              const mainVariation = product.product_variations.find(v => v.main);
              const selectedVariation = chosenVariation || mainVariation || product.product_variations[0];
              initialVariations[product.id] = selectedVariation;
            }
          });
          setSelectedVariations(initialVariations);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [endpoint, filters, location.search]); // Include location.search but skip fetch if only scrollTo changed

  // Separate effect to handle scrollTo reordering (runs after products are loaded)
  useEffect(() => {
    if (products.length === 0 || loading) return;
    
    const urlParams = new URLSearchParams(location.search);
    const scrollToFromUrl = urlParams.get('scrollTo');
    const scrollToProductId = locationState.scrollToProductId || scrollToFromUrl;
    
    // Only handle if we have a scrollTo and haven't handled this specific one yet
    if (scrollToProductId && lastHandledScrollToRef.current !== scrollToProductId) {
      const productIndex = products.findIndex(p => p.id === parseInt(scrollToProductId));
      if (productIndex > 0) {
        // Move the clicked product to the front
        const clickedProduct = products[productIndex];
        const reorderedProducts = [clickedProduct, ...products.slice(0, productIndex), ...products.slice(productIndex + 1)];
        setProducts(reorderedProducts);
      }
      
      // Mark as handled and clear URL parameter
      lastHandledScrollToRef.current = scrollToProductId;
      setTimeout(() => {
        const cleanPath = location.pathname;
        const cleanSearch = location.search.replace(/[?&]scrollTo=[^&]*/g, '').replace(/^&/, '?').replace(/^\?$/, '');
        if (cleanSearch !== location.search) {
          navigate(cleanPath + cleanSearch, { replace: true, state: {} });
        }
      }, 50);
    } else if (!scrollToProductId && lastHandledScrollToRef.current) {
      // Reset ref when scrollTo is cleared
      lastHandledScrollToRef.current = null;
    }
  }, [products, loading, location.search, locationState.scrollToProductId, location.pathname, navigate]);


  const incrementQuantity = (product, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    const variationId =
      product.chosen_variation?.id || product.product_variations?.[0]?.id || null;
    axios
      .post(`/api/v1/cart/increase_quantity`, {
        product_id: product.id,
        product_variation_id: variationId,
      })
      .then((response) => {
        setQuantities((prev) => ({ ...prev, [product.id]: response.data.quantity }));
      })
      .catch((error) => console.error('Error increasing quantity:', error));
  };

  const decrementQuantity = (product, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    const currentQty = quantities[product.id] !== undefined 
      ? quantities[product.id] 
      : product.chosen_variation?.quantity || 1;
    if (cartMode && currentQty === 1) {
      // Виклик підтвердження видалення з використанням дії 'decrease'.
      setDeleteProduct(product);
      setDeleteAction('decrease');
      setShowDeletePopup(true);
      return;
    }
    const variationId =
      product.chosen_variation?.id || product.product_variations?.[0]?.id || null;
    axios
      .post(`/api/v1/cart/decrease_quantity`, {
        product_id: product.id,
        product_variation_id: variationId,
      })
      .then((response) => {
        setQuantities((prev) => ({ ...prev, [product.id]: response.data.quantity }));
      })
      .catch((error) => console.error('Error decreasing quantity:', error));
  };

  const openInfo = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(product.id);
  };

  const closeInfo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(null);
  };

  const handleMobileCardClick = (productId, e) => {
    // Detect mobile device
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) {
      // Desktop behavior - allow navigation
      return true;
    }
    
    // Mobile behavior - prevent navigation and handle double-click for like
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const lastClick = clickTimeouts[productId];
    
    if (lastClick && now - lastClick < 300) {
      // Double click detected - toggle like
      clearTimeout(clickTimeouts[`timeout_${productId}`]);
      toggleLike(productId, e);
      setClickTimeouts(prev => {
        const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
        return rest;
      });
    } else {
      // Single click - do nothing, just update timestamp
      setClickTimeouts(prev => ({
        ...prev,
        [productId]: now
      }));
      
      // Set timeout to clear the click timestamp
      const timeoutId = setTimeout(() => {
        setClickTimeouts(prev => {
          const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
          return rest;
        });
      }, 300);
      
      setClickTimeouts(prev => ({
        ...prev,
        [`timeout_${productId}`]: timeoutId
      }));
    }
    
    return false;
  };

  const handleNewButton = (product, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Always navigate to the account profile - works for both logged in and logged out users
    // account_name should always be available from the products endpoint
    if (product.account_name) {
      // Navigate using account_name (already formatted like @tearoom)
      navigate(`/${product.account_name}`);
    } else if (product.account_id) {
      // Fallback: if account_name is missing, navigate to tearoom
      // In most cases, account_name should be present from the products endpoint
      navigate('/@tearoom');
    } else {
      // Final fallback
      navigate('/@tearoom');
    }
  };

  const toggleLike = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    axios
      .post(`/api/v1/products/${id}/toggle_like`)
      .then((response) => {
        setLikedIds((prev) => {
          if (response.data.liked) {
            return [...prev, id];
          } else {
            return prev.filter((x) => x !== id);
          }
        });
      })
      .catch((error) => console.error('Error toggling like:', error));
  };

  const handleShare = async (productId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const productUrl = `${window.location.origin}/product/${productId}`;
    
    try {
      // Try to use the Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(productUrl);
        addToast('Посилання скопійовано!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = productUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        addToast('Посилання скопійовано!');
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      addToast('Помилка копіювання');
    }
  };

  const addToast = (message) => {
    const toastId = Date.now() + Math.random();
    const newToast = {
      id: toastId,
      message: message,
      hiding: false
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Hide toast after 2.5 seconds
    setTimeout(() => {
      setToasts(prev => prev.map(toast => 
        toast.id === toastId ? { ...toast, hiding: true } : toast
      ));
      
      // Remove toast after animation
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== toastId));
      }, 300);
    }, 2500);
  };

  const toggleCart = (product, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    // У режимі кошику, якщо товар вже в кошику, викликаємо підтвердження видалення з використанням дії 'toggle'.
    if (cartMode && cartIds.includes(product.id)) {
      setDeleteProduct(product);
      setDeleteAction('toggle');
      setShowDeletePopup(true);
      return;
    }
    // Show buy dialog instead of directly adding to cart
    setBuyDialogProduct(product);
    setBuyDialogQuantity(1);
    setShowBuyDialog(true);
  };
  
  const handleBuyDialogConfirm = () => {
    if (!buyDialogProduct || !buyDialogProduct.product_variations || buyDialogProduct.product_variations.length === 0) {
      return;
    }
    
    // Check if user is trying to buy from themselves
    if (buyDialogProduct.account_id && currentUserAccountId && 
        String(buyDialogProduct.account_id) === String(currentUserAccountId)) {
      addToast('Покупка у себе не дозволена правилами');
      return;
    }
    
    const quantity = buyDialogQuantity || 0;
    if (quantity <= 0) {
      addToast('Введіть кількість');
      return;
    }
    
    const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
    if (!variation) {
      addToast('Помилка: варіанти товару недоступні');
      return;
    }
    
    // Quantity is number of portions (each portion is 10g)
    const itemsToAdd = quantity;
    
    // Add to cart with quantity
    const addToCartPromises = Array.from({ length: itemsToAdd }, () =>
      axios.post(`/api/v1/cart/add_to_cart`, { product_variation_id: variation.id })
    );
    
    Promise.all(addToCartPromises)
      .then(() => {
        setCartIds((prev) => [...prev, buyDialogProduct.id]);
        setQuantities((prev) => ({ ...prev, [buyDialogProduct.id]: itemsToAdd }));
        setShowBuyDialog(false);
        addToast(`Додано ${quantity} порцій (${quantity * 10}g) в кошик!`);
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        addToast('Помилка додавання в кошик');
      });
  };

  // Обробник вибору варіації у спливаючому вікні.
  const handleVariationSelect = (variation) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    axios
      .post(`/api/v1/cart/add_to_cart`, {
        product_variation_id: variation.id,
      })
      .then((response) => {
        setCartIds((prev) => [...prev, popupProductId]);
        setQuantities((prev) => ({ ...prev, [popupProductId]: 1 }));
        setShowVariationPopup(false);
        setPopupProductId(null);
        
        // Show toast notification
        addToast(`Додано ${formatSizeSpaced(variation.size)} в кошик!`);
      })
      .catch((error) => console.error('Error adding to cart:', error));
  };

  // Підтвердження видалення: залежно від deleteAction викликаємо відповідний endpoint.
  const confirmDeletion = () => {
    if (!deleteProduct) return;
    const variationId =
      deleteProduct.chosen_variation?.id || deleteProduct.product_variations?.[0]?.id || null;
    if (deleteAction === 'decrease') {
      axios
        .post(`/api/v1/cart/decrease_quantity`, {
          product_id: deleteProduct.id,
          product_variation_id: variationId,
        })
        .then((response) => {
          if (response.data.quantity === 0) {
            setCartIds((prev) => prev.filter((id) => id !== deleteProduct.id));
            setQuantities((prev) => {
              const newQuantities = { ...prev };
              delete newQuantities[deleteProduct.id];
              return newQuantities;
            });
            // Видаляємо товар з UI.
            setProducts((prev) => prev.filter((p) => p.id !== deleteProduct.id));
          }
          setShowDeletePopup(false);
          setDeleteProduct(null);
        })
        .catch((error) => console.error('Error decreasing quantity:', error));
    } else if (deleteAction === 'toggle') {
      axios
        .post(`/api/v1/cart/toggle_item`, {
          product_id: deleteProduct.id
        })
        .then((response) => {
          if (!response.data.in_cart) {
            setCartIds((prev) => prev.filter((id) => id !== deleteProduct.id));
            setQuantities((prev) => {
              const newQuantities = { ...prev };
              delete newQuantities[deleteProduct.id];
              return newQuantities;
            });
            // Видаляємо товар з UI.
            setProducts((prev) => prev.filter((p) => p.id !== deleteProduct.id));
          }
          setShowDeletePopup(false);
          setDeleteProduct(null);
        })
        .catch((error) => console.error('Error toggling cart:', error));
    }
  };

  const cancelDeletion = () => {
    setShowDeletePopup(false);
    setDeleteProduct(null);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <div className="products__page" style={cartMode ? { paddingBottom: '120px' } : undefined}>
      {!cartMode && !isLikedMode && (
        <FilterMenu
          onFilterChange={handleFilterChange}
          categories={categories}
        />
      )}
      
      {loading ? (
        <div className="loading-indicator">
          <div className="loader"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="no-products-message">
          {cartMode ? (
            <p>У вашому кошику поки немає товарів.</p>
          ) : isLikedMode ? (
            <p>Поки немає обраних товарів.</p>
          ) : (
            <p>Ой! Наразі немає товарів для відображення. Будь ласка, спробуйте пізніше..</p>
          )}
        </div>
      ) : (
        <>
          <div className="products__grid">
            {products.map((product, index) => {
              const imageUrl = getProductImageUrl(product);
              (() => {
                if (!product.product_variations || product.product_variations.length === 0) {
                  return { originalPrice: 'Н/Д' };
                }
                
                let selectedVariation;
                
                // In cart mode, use the chosen variation if available
                if (cartMode && product.chosen_variation && product.chosen_variation.price) {
                  selectedVariation = product.chosen_variation;
                } else if (selectedVariations[product.id]) {
                  // Use the variation selected by the user in the inline selector
                  selectedVariation = selectedVariations[product.id];
                } else {
                  // Otherwise use the first variation or main variation
                  selectedVariation = product.product_variations.find(v => v.main) || product.product_variations[0];
                }
                
                return {
                  originalPrice: selectedVariation.price
                };
              })();
                          const isLiked = likedIds.includes(product.id);
            const inCart = cartIds.includes(product.id);
              const currentQuantity = quantities[product.id] !== undefined
                ? quantities[product.id]
                : product.chosen_variation?.quantity || 1;

              // Add ref to the last product for infinite scroll
              const isLastProduct = index === products.length - 1;
              
              const isFlipped = flippedProductId === product.id;
              
              return (
                <a 
                  key={product.id} 
                  href={`/product/${product.id}`} 
                  className="products__link"
                  data-product-id={product.id}
                  ref={isLastProduct ? lastProductRef : null}
                  onClick={(e) => handleMobileCardClick(product.id, e)}
                >
                  <div className={`products__item ${isFlipped ? 'products__item--flipped' : ''}`}>
                    <div
                      className="products__item-background"
                      style={{ backgroundImage: `url(${imageUrl})` }}
                    ></div>
                    <div
                      className="products__item-foreground"
                      style={{ backgroundImage: `url(${imageUrl})` }}
                    ></div>
                    <div
                      className={`feed-caption-scrim${activeProductId === product.id ? ' feed-caption-scrim--active' : ''}`}
                      aria-hidden="true"
                    ></div>
                    {cartMode && (
                      <>
                        <div className="products__corner products__corner-topleft">
                          <button
                            className="products__quantity-btn--plus"
                            onClick={(e) => incrementQuantity(product, e)}
                          >
                            +
                          </button>
                        </div>
                        <div className="products__corner products__corner-bottomleft">
                          <button
                            className="products__quantity-btn--minus"
                            onClick={(e) => decrementQuantity(product, e)}
                          >
                            −
                          </button>
                        </div>
                        <div className="products__quantity-display">
                          {currentQuantity}
                        </div>
                      </>
                    )}
                    {/* New button above view and like buttons */}
                    <div className="products__corner products__corner-topright products__corner-topright-new">
                      <button
                        className="products__new-toggle"
                        onClick={(e) => handleNewButton(product, e)}
                        aria-label="Product image"
                        style={
                          product.account_url ? {
                            backgroundImage: `url(${completeImageUrl(product.account_url)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.4)',
                            backdropFilter: 'blur(4px)'
                          }
                        }
                      >
                        {!product.account_url && (
                          <span className="products__new-toggle-name">{product.account_title || (product.account_name || '').replace(/^@/, '') || 'Магазин'}</span>
                        )}
                      </button>
                    </div>
                    {/* Info button above like */}
                    <div className="products__corner products__corner-topright products__corner-topright-info">
                      <button
                        className="products__info-toggle"
                        onClick={(e) => isFlipped ? closeInfo(e) : openInfo(product, e)}
                        aria-label={isFlipped ? "Close info" : "Product info"}
                      >
                        {isFlipped ? '×' : <VisibilityOutlined className="products__info-icon-svg" />}
                      </button>
                    </div>
                    <div className="products__corner products__corner-topright">
                      <button
                        className={`products__toggle ${isLiked ? 'liked' : 'unliked'}`}
                        onClick={(e) => toggleLike(product.id, e)}
                      >
                        <HeartIcon />
                      </button>
                    </div>
                    {/* Share button */}
                    <div className="products__corner products__corner-middle-right">
                      <button
                        className="products__share-toggle"
                        onClick={(e) => handleShare(product.id, e)}
                        aria-label="Share product"
                      >
                        <Reply className="products__share-icon" style={{ transform: 'scaleX(-1)' }} />
                      </button>
                    </div>
                    <div className="products__corner products__corner-bottomright">
                      <button
                        className={`products__cart-toggle ${inCart ? 'inCart' : 'outCart'}`}
                        onClick={(e) => toggleCart(product, e)}
                      >
                        <ShoppingCartIcon className="products__cart-icon" />
                      </button>
                    </div>
                    {/* Product name and price caption - freestanding layer over the image, no card */}
                    {product.product_variations && product.product_variations.length > 0 && (
                      <div className="feed-caption">
                        <span className="feed-caption__name">
                          {product.title}
                        </span>
                        <FeedCaptionMeta variation={product.product_variations.find(v => v.main) || product.product_variations[0]} />
                      </div>
                    )}
                    
                    {/* Back side - Product info */}
                    <div className="products__item-back">
                      <ProductInfoContent product={product} />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
          {/* Loading indicator for infinite scroll */}
          {loadingMore && (
            <div className="loading-more-indicator">
              <div className="loader"></div>
              <p>Завантаження більше товарів...</p>
            </div>
          )}
        </>
      )}
      {showVariationPopup && (
        <VariationPopup
          variations={popupVariations}
          onSelect={handleVariationSelect}
          onClose={() => setShowVariationPopup(false)}
        />
      )}
      {showDeletePopup && (
        <ConfirmPopup
          message="Видалити цей товар з кошику?"
          onConfirm={confirmDeletion}
          onCancel={cancelDeletion}
        />
      )}
      {showBuyDialog && buyDialogProduct && (
        <div className="auth-popup-overlay" onClick={() => setShowBuyDialog(false)}>
          <div className="auth-popup-content" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div style={{ textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) - 1;
                      setBuyDialogQuantity(Math.max(0, newQty));
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={buyDialogQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setBuyDialogQuantity(Math.max(0, val));
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      padding: '0 8px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) + 1;
                      setBuyDialogQuantity(newQty);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const quantity = buyDialogQuantity || 0;
                      const grams = quantity * 10;
                      return grams > 0 ? `${grams}G` : '0';
                    })()}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: 'var(--color-accent)',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
                      if (!variation) return '0';
                      const basePrice = parseFloat(variation.price) || 0;
                      const quantity = buyDialogQuantity || 0;
                      // Quantity is number of portions (each portion is 10g)
                      const totalPrice = quantity * basePrice;
                      return totalPrice % 1 === 0 ? totalPrice.toString() : totalPrice.toFixed(2);
                    })()} ₴
                  </div>
                </div>
                {buyDialogProduct && buyDialogProduct.account_id && currentUserAccountId && 
                 String(buyDialogProduct.account_id) === String(currentUserAccountId) && (
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#fff5f8',
                    borderRadius: '8px'
                  }}>
                    Покупка у себе не дозволена правилами
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button 
                  onClick={handleBuyDialogConfirm}
                  disabled={(buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? '#ccc' : 'var(--color-accent)',
                    border: 'none',
                    cursor: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'none' : '0 4px 12px rgba(31, 31, 31, 0.3)',
                    transition: 'all 0.3s ease',
                    marginRight: '20px',
                    marginBottom: '20px',
                    opacity: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#fff', fontSize: '28px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toasts.map((toast, index) => (
        <div 
          key={toast.id}
          className={`toast-notification ${toast.hiding ? 'toast-hiding' : ''}`}
          style={{ top: `${20 + index * 70}px` }}
        >
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}
